import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ChecklistSection } from '../../../types';
import Spinner from '../../ui/Spinner';
import PackingItemRow, { type PackingMember } from './PackingItemRow';
import {
  Trash2, GripVertical, Sparkles, Lock, Icon, type LucideIcon,
} from '../../ui/Icon';

export interface SectionMeta {
  Icon: LucideIcon;
  color: string;
  bg: string;
  accent: string;
}

interface Props {
  section: ChecklistSection;
  meta: SectionMeta;
  aiOpen: boolean;
  /** This section is the one currently generating items. */
  generating: boolean;
  /** Some section (this one or another) is generating — AI actions stay locked. */
  aiBusy: boolean;
  onToggleAi: () => void;
  onCloseAi: () => void;
  /** Resolves true when items were added, so the card can clear its input. */
  onGenerate: (info: string) => Promise<boolean>;
  onDeleteSection: () => void;
  onToggleItem: (itemId: string, checked: boolean) => void;
  onDeleteItem: (itemId: string) => void;
  onAddItem: (text: string) => Promise<void>;
  onRenameItem: (itemId: string, text: string) => void;
  onAssignItem: (itemId: string, userId: string | null) => void;
  /** Someone else's suitcase — shown so the group can coordinate, never edited. */
  readOnly: boolean;
  /** Members offered as "who brings this", on shared sections only. */
  members: PackingMember[];
}

export default function PackingSectionCard({
  section, meta, aiOpen, generating, aiBusy,
  onToggleAi, onCloseAi, onGenerate, onDeleteSection,
  onToggleItem, onDeleteItem, onAddItem, onAssignItem, onRenameItem,
  readOnly, members,
}: Props) {
  const { t } = useTranslation();
  const [newItem, setNewItem] = useState('');
  const [aiInput, setAiInput] = useState('');

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: section.id });

  const done    = section.items.filter(i => i.checked).length;
  const total   = section.items.length;
  const allDone = total > 0 && done === total;
  const pct     = total > 0 ? Math.round((done / total) * 100) : 0;

  // Lo ya metido en la maleta sube arriba, lo que falta queda al final. Sort es
  // estable, así que dentro de cada grupo se respeta el sortOrder del servidor.
  const orderedItems = useMemo(
    () => [...section.items].sort((a, b) => Number(b.checked) - Number(a.checked)),
    [section.items],
  );

  async function addItem() {
    const text = newItem.trim();
    if (!text) return;
    await onAddItem(text);
    setNewItem('');
  }

  async function generate() {
    if (await onGenerate(aiInput.trim())) setAiInput('');
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        zIndex: isDragging ? 10 : undefined,
      }}
      className={`bg-white dark:bg-slate-900/60 rounded-2xl border overflow-hidden flex flex-col transition-colors ${
        allDone
          ? 'border-green-200 dark:border-green-900/60'
          : 'border-slate-200 dark:border-slate-800'
      }`}
    >
      <div
        className="h-1.5 w-full shrink-0 dark:opacity-70"
        style={{ backgroundColor: allDone ? '#22c55e' : meta.accent }}
      />

      <div className="p-4 flex flex-col gap-3">
        <div className="flex items-start gap-2">
          {/* Dedicated handle: the card is full of inputs and checkboxes, so
              dragging is opt-in from here instead of the whole surface. The
              order is stored on the row, so dragging someone else's section
              would move it for them too — hence no handle at all when read-only. */}
          {readOnly ? (
            <span className="w-5 h-9 -ml-1 shrink-0 flex items-center justify-center text-slate-300 dark:text-slate-600" title={t('packing.readOnlyHint')}>
              <Icon as={Lock} size={12} />
            </span>
          ) : (
            <button
              {...attributes} {...listeners}
              className="w-5 h-9 -ml-1 shrink-0 flex items-center justify-center text-slate-300 hover:text-slate-500 dark:text-slate-600 dark:hover:text-slate-400 cursor-grab active:cursor-grabbing touch-none"
              aria-label={t('packing.dragHandle')}
              onClick={e => e.stopPropagation()}
            >
              <Icon as={GripVertical} size={14} />
            </button>
          )}
          <div className={`w-9 h-9 rounded-xl ${meta.bg} flex items-center justify-center shrink-0`}>
            <Icon as={meta.Icon} size={16} className={meta.color} />
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            <h3 className="font-bold text-sm leading-tight text-slate-900 dark:text-slate-100 truncate">{section.title}</h3>
            {total > 0 && (
              <p className="text-xs font-medium mt-0.5" style={{ color: allDone ? '#16a34a' : meta.accent }}>
                {allDone ? t('packing.done') : `${done}/${total} · ${pct}%`}
              </p>
            )}
          </div>
          {/* Not rendered rather than hidden with a class: a `hidden` button is
              still in the DOM, still focusable and still fires its handler. */}
          {!readOnly && (
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={onToggleAi}
              className={`w-6 h-6 rounded-lg flex items-center justify-center cursor-pointer transition-colors ${
                aiOpen
                  ? 'bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300'
                  : 'bg-slate-100 hover:bg-violet-100 dark:bg-slate-800 dark:hover:bg-violet-900/30 text-slate-500 hover:text-violet-600'
              }`}
              aria-label={t('packing.sectionAiButton')}
            >
              <Icon as={Sparkles} size={12} />
            </button>
            <button
              onClick={onDeleteSection}
              className="w-6 h-6 rounded-lg bg-slate-100 hover:bg-red-100 dark:bg-slate-800 dark:hover:bg-red-900/30 flex items-center justify-center cursor-pointer transition-colors"
              aria-label={t('packing.deleteSection')}
            >
              <Icon as={Trash2} size={12} className="text-slate-500 hover:text-red-500" />
            </button>
          </div>
          )}
        </div>

        {aiOpen && (
          <div className="rounded-xl border border-violet-100 dark:border-violet-900/40 bg-violet-50/40 dark:bg-violet-900/10 p-2.5 flex flex-col gap-2">
            <input
              type="text"
              className="input text-xs py-1.5 bg-white dark:bg-slate-800"
              placeholder={t('packing.sectionAiPlaceholder')}
              value={aiInput}
              onChange={e => setAiInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !aiBusy && generate()}
              disabled={generating}
            />
            <div className="flex gap-2">
              <button
                onClick={generate}
                disabled={aiBusy}
                className="flex-1 h-7 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-xs font-semibold flex items-center justify-center gap-1.5 cursor-pointer transition-colors"
              >
                {generating ? <Spinner size="sm" /> : <Icon as={Sparkles} size={12} />}
                {t('packing.sectionAiGenerate')}
              </button>
              <button
                onClick={onCloseAi}
                disabled={generating}
                className="h-7 px-2.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-xs cursor-pointer disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        {total > 0 && (
          <div className="h-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${pct}%`, backgroundColor: allDone ? '#22c55e' : meta.accent }}
            />
          </div>
        )}

        <div className="space-y-1.5">
          {orderedItems.map(item => (
            <PackingItemRow
              key={item.id}
              item={item}
              readOnly={readOnly}
              assignable={section.ownerUserId === null}
              members={members}
              onToggle={checked => onToggleItem(item.id, checked)}
              onDelete={() => onDeleteItem(item.id)}
              onAssign={userId => onAssignItem(item.id, userId)}
              onRename={text => onRenameItem(item.id, text)}
            />
          ))}
          {readOnly && section.items.length === 0 && (
            <p className="text-xs text-slate-400 dark:text-slate-500 italic">{t('packing.emptySection')}</p>
          )}
        </div>

        {!readOnly && (
        <div className="flex gap-2 pt-1 border-t border-slate-50 dark:border-slate-800">
          <input
            type="text"
            className="input flex-1 text-sm py-1.5 mt-2.5"
            placeholder={t('packing.addItemPlaceholder')}
            value={newItem}
            onChange={e => setNewItem(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addItem()}
          />
          <button onClick={addItem} className="btn-secondary py-1.5 px-3 text-xs mt-2.5">
            {t('packing.addItemBtn')}
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
